import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { OrganisationType, TradingRelationshipDetail, WorkflowDetail } from 'api'
import { Translation, Locale } from 'locale'
import { gridOptionsBase, TypedValueGetterParams, withAllContext, createActionsRenderer } from 'view/components/datagrid'
import { TradingEntityRelationshipsPanelActionsButton } from './TradingEntityRelationshipsPanelActionsButton'
import { booleanFormatter } from 'view/shared/datagrid'

enum TradingEntityRelationshipsPanelGridColumnId {
  ORGANISATION_NAME = 'ORGANISATION_NAME',
  TRADING_RELATIONSHIP_ACTIONS = 'TRADING_RELATIONSHIP_ACTIONS',
  TRADING_ENTITY = 'TRADING_ENTITY',
  US_SPONSORED_CLEARING = 'US_SPONSORED_CLEARING',
  US_SPONSORED_CLEARING_DEFAULT = 'US_SPONSORED_CLEARING_DEFAULT',
  MASTER_LEGAL_AGREEMENTS = 'MASTER_LEGAL_AGREEMENTS',
  DEFAULT_AGREEMENT = 'DEFAULT_AGREEMENT',
  INITIATOR_FICC_TO_INITIATOR_FIX = 'INITIATOR_FICC_TO_INITIATOR_FIX',
  INITIATOR_FICC_TO_RESPONDER_FIX = 'INITIATOR_FICC_TO_RESPONDER_FIX',
  RESPONDER_FICC_TO_INITIATOR_FIX = 'RESPONDER_FICC_TO_INITIATOR_FIX',
  RESPONDER_FICC_TO_RESPONDER_FIX = 'RESPONDER_FICC_TO_RESPONDER_FIX'
}

export const tradingEntityRelationshipsPanelColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.TRADING_RELATIONSHIP_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 130,
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(TradingEntityRelationshipsPanelActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.ORGANISATION_NAME,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data[params.data.organisationType === OrganisationType.BUY_SIDE ? 'sellSideOrg' : 'buySideOrg']?.legalName,
    headerName: Locale.translate(Translation.TC_ORGANISATION),
    width: 175,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.TRADING_ENTITY,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data &&
      params.data[params.data.organisationType === OrganisationType.BUY_SIDE ? 'sellTradingEntity' : 'buyTradingEntity']?.publishedName,
    headerName: Locale.translate(Translation.TC_TRADING_ENTITY),
    width: 175,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.US_SPONSORED_CLEARING,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.usSponsoredClearing,
    headerName: Locale.translate(Translation.TC_US_SPONSORED_CLEARING),
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.US_SPONSORED_CLEARING_DEFAULT,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.usSponsoredClearingByDefault,
    headerName: Locale.translate(Translation.TC_US_SPONSORED_CLEARING_BY_DEFAULT),
    width: 80,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.MASTER_LEGAL_AGREEMENTS,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.masterLegalAgreements.join(', '),
    headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENTS),
    width: 175,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.DEFAULT_AGREEMENT,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.defaultAgreement,
    headerName: Locale.translate(Translation.TC_DEFAULT_AGREEMENT),
    width: 135,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.INITIATOR_FICC_TO_INITIATOR_FIX,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.initiatorFiccToInitiatorFix,
    headerName: Locale.translate(Translation.TC_INITIATOR_FICC_TO_INITIATOR_FIX_ABB),
    width: 135,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.INITIATOR_FICC_TO_RESPONDER_FIX,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.initiatorFiccToResponderFix,
    headerName: Locale.translate(Translation.TC_INITIATOR_FICC_TO_RESPONDER_FIX_ABB),
    width: 135,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.RESPONDER_FICC_TO_INITIATOR_FIX,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.responderFiccToInitiatorFix,
    headerName: Locale.translate(Translation.TC_RESPONDER_FICC_TO_INITIATOR_FIX_ABB),
    width: 135,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: TradingEntityRelationshipsPanelGridColumnId.RESPONDER_FICC_TO_RESPONDER_FIX,
    valueGetter: (params: TypedValueGetterParams<WorkflowDetail<TradingRelationshipDetail>>) =>
      params.data && params.data.tradingRelationship.responderFiccToResponderFix,
    headerName: Locale.translate(Translation.TC_RESPONDER_FICC_TO_RESPONDER_FIX_ABB),
    width: 135,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  }
]

export const tradingEntityRelationshipsGridOptions: GridOptions<WorkflowDetail<TradingRelationshipDetail>> = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.tradingRelationship.id,
  suppressMaintainUnsortedOrder: true
}
